import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import TextField from "@/components/ui/Textfield";
import PopoverMenu from "@/components/ui/Popeover";
import { BiSearch, BiTrash } from "react-icons/bi";
import { BsPlus } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { Settings, Trash } from "lucide-react";
import useGlobalStore from "@/store/globalStore";
import useObjectiveStore from "./store/objectiveStore";
import { getColorByHash } from "@/helper/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";

export default function ManageObjectives() {
  const navigate = useNavigate();
  const {
    objectives,
    getObjectives,
    deleteObjective,
    setFormDataForEdit,
    resetFormData,
    sortField,
  } = useObjectiveStore();

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [objectiveToDelete, setObjectiveToDelete] = useState<any>(null);

  const showError = useGlobalStore((state) => state.showError);
  const showSuccess = useGlobalStore((state) => state.showSuccess);

  const itemsPerPage = 25;

  const filteredObjectives = objectives?.filter((obj) =>
    obj?.name?.toLowerCase().includes(search.toLowerCase())
  ) || [];

  const totalPages = Math.ceil(filteredObjectives.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentObjectives = filteredObjectives.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const goPrevious = () => setCurrentPage((page) => Math.max(page - 1, 1));
  const goNext = () => setCurrentPage((page) => Math.min(page + 1, totalPages));

  const fetchObjective = async (sort?: string) => {
    try {
      await getObjectives(sort);
    } catch (error: any) {
      showError(error?.message ?? "Something went wrong");
    }
  };

  const handleSort = (field: string) => {
    let newSort: string;
    if (sortField === field) {
      newSort = `-${field}`;
    } else if (sortField === `-${field}`) {
      newSort = '';
    } else {
      newSort = field;
    }
    setCurrentPage(1);
    fetchObjective(newSort || undefined);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField === field) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 15l6-6 6 6" />
        </svg>
      );
    }
    if (sortField === `-${field}`) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    }
    return null;
  };

  useEffect(() => {
    fetchObjective();
  }, []);

  const handleDelete = async () => {
    if (!objectiveToDelete) return;

    try {
      await deleteObjective(objectiveToDelete.id);
      showSuccess("Objective deleted successfully");
    } catch (error: any) {
      showError(error?.message ?? "Failed to delete objective");
    } finally {
      setDialogOpen(false);
      setObjectiveToDelete(null);
    }
  };

  return (
    <Layout>
      <div>
        <h1 className="text-2xl font-bold mb-8">Manage Objectives</h1>

        <div className="flex justify-between mb-6">
          <TextField
            type="text"
            leadingIcon={<BiSearch />}
            placeholder="Search by keyword, name, etc."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="w-96"
          />
          <Button
            type="primary"
            trailingIcon={<BsPlus fontSize={25} />}
            onClick={() => {
              resetFormData();
              navigate("/objectives/create");
            }}
          >
            Add New Objective
          </Button>
        </div>

        <div className="grid grid-cols-12 border-b border-gray-300 pb-2 mb-2 text-sm text-gray-700 select-none">
          <div className="col-span-11 flex items-center cursor-pointer gap-1" onClick={() => handleSort('name')}>
            Name
            <SortIcon field="name" />
          </div>
          <div className="col-span-1"></div>
        </div>

        <div className="divide-y divide-gray-300">
          {currentObjectives.map((item) => (
            <div key={item.id} className="grid grid-cols-12 py-3 text-sm">
              <div className="col-span-11 flex items-center gap-3">
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-full text-white font-semibold text-lg ${getColorByHash(
                    item.name
                  )}`}
                >
                  {item.name.slice(0, 1).toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <div className="font-semibold">{item.name}</div>
                  <div className="text-sm text-gray-500">
                    {item.desc || "No description"}
                  </div>
                </div>
              </div>
              <div className="flex col-span-1 justify-end">
                <PopoverMenu
                  items={[
                    {
                      label: "Edit Objective Info",
                      showDivider: true,
                      icon: <Settings height={18} width={18} />,
                      onClick: () => {
                        setFormDataForEdit(item);
                        navigate(`/objectives/create?id=${item.id}`);
                      },
                    },
                    {
                      label: "Delete Objective",
                      showDivider: false,
                      icon: <Trash height={18} width={18} />,
                      onClick: () => {
                        setObjectiveToDelete(item);
                        setDialogOpen(true);
                      },
                    },
                  ]}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end items-center mt-6 space-x-4 text-sm">
          <Button
            type="secondary"
            onClick={goPrevious}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button
            type="secondary"
            onClick={goNext}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </Button>
        </div>

        {/* Delete Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-xl bg-white">
            <DialogHeader>
              <div className="font-semibold text-xl text-center">
                Delete Objective
              </div>
              <DialogDescription className="mt-4 text-center text-gray-600">
                Are you sure you want to delete{" "}
                <span className="font-semibold">{objectiveToDelete?.name}</span>?
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex justify-center gap-4 mt-6">
              <Button
                type="secondary"
                trailingIcon={<BiTrash fontSize={18} />}
                onClick={handleDelete}
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
